import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, HandHeart, Loader2, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import api, { API } from "../lib/api";

const NAVY = "#0d2340";
const GOLD = "#C9973A";
const BG = "/landing-bg.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Landing() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [featured, setFeatured] = useState([]);
  const lastOrg = typeof window !== "undefined" ? localStorage.getItem("mc_org_slug") : null;

  useEffect(() => {
    api.get("/public/org-featured").then(({ data }) => setFeatured(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const q = code.trim();
    if (q.length < 2) { setResults([]); setOpen(false); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/public/org-search`, { params: { q } });
        setResults(data || []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [code]);

  const goToMission = (e) => {
    e?.preventDefault?.();
    if (results.length) return navigate(`/${results[0].slug}`);
    const slug = code.trim().toLowerCase().replace(/\s+/g, "-").replace(/^\/+|\/+$/g, "");
    if (slug) navigate(`/${slug}`);
  };

  return (
    <div className="relative min-h-[100dvh] w-full overflow-hidden" style={{ backgroundColor: NAVY }} data-testid="landing-root">
      {/* Background image */}
      <img src={BG} alt="A couple walking toward a mountain sunrise" className="absolute inset-0 h-full w-full object-cover" />
      {/* Readability overlays */}
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(13,35,64,0.55) 0%, rgba(13,35,64,0.35) 40%, rgba(13,35,64,0.82) 100%)" }} />
      <div className="absolute inset-0" style={{ background: "radial-gradient(120% 80% at 50% 0%, transparent 40%, rgba(13,35,64,0.5) 100%)" }} />

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-6 sm:px-10 pt-6">
        <div className="flex items-center gap-2.5" data-testid="landing-brand">
          <img src="/icon-192.png" alt="Mission Companion" className="h-9 w-9 rounded-xl shadow-lg" />
          <span className="font-heading font-bold text-white text-lg tracking-tight">Mission Companion</span>
        </div>
        <button
          data-testid="landing-marketing-link"
          onClick={() => navigate("/marketing")}
          className="text-sm text-white/70 hover:text-white transition-colors hidden sm:block"
        >
          Learn more
        </button>
      </div>

      {/* Hero content */}
      <div className="relative z-10 flex min-h-[calc(100dvh-88px)] flex-col justify-end sm:justify-center px-6 sm:px-10 pb-12 sm:pb-0 max-w-2xl">
        <motion.span
          variants={fadeUp} initial="hidden" animate="show"
          className="inline-flex w-fit items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white/90 mb-5"
          style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)" }}
        >
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: GOLD }} /> The mission companion
        </motion.span>

        <motion.h1
          variants={fadeUp} initial="hidden" animate="show" custom={1}
          className="font-heading font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
        >
          Stay connected to<br className="hidden sm:block" /> your <span style={{ color: GOLD }}>mission.</span>
        </motion.h1>

        <motion.p
          variants={fadeUp} initial="hidden" animate="show" custom={2}
          className="mt-5 text-lg text-white/85 leading-relaxed max-w-md"
        >
          Follow the journey — dispatches, prayer, giving and the itinerary, all in one place.
        </motion.p>

        {/* Actions */}
        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-8 space-y-4">
          {lastOrg && (
            <Button
              data-testid="landing-continue-btn"
              onClick={() => navigate(`/${lastOrg}`)}
              className="w-full sm:w-auto rounded-full text-white h-13 px-8 text-base font-semibold shadow-xl hover:brightness-95 hover:-translate-y-0.5 transition-[filter,transform]"
              style={{ backgroundColor: GOLD, height: 52 }}
            >
              Continue to your mission <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="landing-signin-btn"
              onClick={() => navigate("/auth")}
              className="rounded-full text-white h-13 px-8 text-base font-semibold shadow-xl hover:brightness-95 hover:-translate-y-0.5 transition-[filter,transform]"
              style={{ backgroundColor: lastOrg ? "rgba(255,255,255,0.14)" : GOLD, height: 52, backdropFilter: lastOrg ? "blur(8px)" : undefined }}
            >
              Sign in
            </Button>
            <Button
              data-testid="landing-start-btn"
              variant="outline"
              onClick={() => navigate("/auth?mode=org")}
              className="rounded-full h-13 px-8 text-base font-semibold border-white/40 text-white bg-white/5 hover:bg-white/15 transition-colors"
              style={{ height: 52 }}
            >
              Start your mission free
            </Button>
          </div>

          {/* Mission search */}
          <form onSubmit={goToMission} className="relative pt-1" data-testid="landing-code-form">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-[#0d2340]/45" />
                <Input
                  data-testid="landing-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onFocus={() => results.length && setOpen(true)}
                  placeholder="Search your mission by name…"
                  className="h-12 rounded-full bg-white/95 border-0 text-[#0d2340] placeholder:text-[#0d2340]/50 pl-11 pr-4"
                  autoComplete="off"
                />
                {searching && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-[#0d2340]/45" />}
              </div>
              <Button
                type="submit"
                data-testid="landing-code-go"
                disabled={!code.trim()}
                className="h-12 w-12 shrink-0 rounded-full p-0 text-white disabled:opacity-40"
                style={{ backgroundColor: NAVY, border: "1px solid rgba(255,255,255,0.25)" }}
                aria-label="Go to mission"
              >
                <ArrowRight className="h-5 w-5" />
              </Button>
            </div>

            {open && code.trim().length >= 2 && (
              <div data-testid="landing-search-results" className="absolute z-20 mt-2 w-[calc(100%-3.5rem)] max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden border border-black/5">
                {results.length === 0 && !searching ? (
                  <div className="px-4 py-4 text-sm text-[#0d2340]/60" data-testid="landing-search-empty">
                    No listed mission matches “{code.trim()}”. If you have a direct link or code, try entering it exactly.
                  </div>
                ) : (
                  results.map((r) => (
                    <button
                      key={r.slug}
                      type="button"
                      data-testid={`landing-search-result-${r.slug}`}
                      onClick={() => navigate(`/${r.slug}`)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#0d2340]/5 transition-colors"
                    >
                      {r.logo_file_id ? (
                        <img src={`${API}/files/${r.logo_file_id}?w=72`} alt="" className="h-9 w-9 rounded-lg object-cover shrink-0" />
                      ) : (
                        <span className="h-9 w-9 rounded-lg shrink-0 flex items-center justify-center text-white font-bold" style={{ background: NAVY }}>{(r.org_name || "?").charAt(0)}</span>
                      )}
                      <span className="min-w-0">
                        <span className="block font-semibold text-[#0d2340] truncate">{r.org_name}</span>
                        {r.tagline ? <span className="block text-xs text-[#0d2340]/55 truncate">{r.tagline}</span> : null}
                      </span>
                      <ArrowRight className="h-4 w-4 text-[#0d2340]/40 ml-auto shrink-0" />
                    </button>
                  ))
                )}
              </div>
            )}
          </form>

          <p className="text-xs text-white/60 flex items-center gap-1.5 pt-1">
            <HandHeart className="h-3.5 w-3.5" style={{ color: GOLD }} /> Passwordless sign-in — we email you a one-time code.
          </p>
        </motion.div>

        {/* Featured missions */}
        {featured.length > 0 && (
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-9" data-testid="landing-featured">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white/55 mb-3">Missions to follow</div>
            <div className="flex gap-2.5 flex-wrap">
              {featured.map((m) => (
                <button
                  key={m.slug}
                  type="button"
                  data-testid={`landing-featured-${m.slug}`}
                  onClick={() => navigate(`/${m.slug}`)}
                  className="group flex items-center gap-2.5 rounded-full pl-2 pr-4 py-2 text-left transition-all hover:-translate-y-0.5"
                  style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.14)" }}
                >
                  {m.logo_file_id ? (
                    <img src={`${API}/files/${m.logo_file_id}?w=64`} alt="" className="h-8 w-8 rounded-full object-cover" />
                  ) : (
                    <span className="h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold text-[#0d2340]" style={{ background: GOLD }}>{(m.org_name || "?").charAt(0)}</span>
                  )}
                  <span className="text-sm font-semibold text-white">{m.org_name}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
